// This component handles doing a full page load during a route change in order to get users
// onto the latest version of our JS. It handles this by periodically checking an API endpoint
// to see if the current deployed version of the app is different than the one in our local redux store.
// It then uses react router’s Prompt API to intercept every route change and if there is a version mismatch,
// and we haven’t already refreshed in the last X hours, then force a full page load during route change.

import React, { useState, useEffect } from 'react';
import { Prompt } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import * as ApplicationAPI from 'Shared/api/Application.api';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

const CHECK_INTERVAL = 3600000; // every hour
// const CHECK_INTERVAL = 5000; // every 5 seconds for testing

export default () => {
  const { forceNewAppVersion: forceNewAppVersionFlag } = useFlags();

  const {
    app: { loadedAt: loadedAppAt, sourceVersion: sourceVersionAtLoad },
  } = useApp();

  const [latestSourceVersion, setLatestSourceVersion] = useState(sourceVersionAtLoad);
  const [shouldForceRefresh, setShouldForceRefresh] = useState(false);

  // Check every hour if there's a new change
  useEffect(() => {
    const fetchLatestSourceVersion = () => {
      ApplicationAPI.fetchCurrentVersion().then(resp => setLatestSourceVersion(resp.data.version));
      setTimeout(fetchLatestSourceVersion, CHECK_INTERVAL);
    };

    setTimeout(fetchLatestSourceVersion, CHECK_INTERVAL);
  });

  useEffect(() => {
    const secondsSinceLoad = Math.floor(Date.now() / 1000) - loadedAppAt;
    const threeDays = 259200;
    const sourceOutOfDate = sourceVersionAtLoad !== latestSourceVersion;

    if (forceNewAppVersionFlag && sourceOutOfDate && secondsSinceLoad > threeDays) {
      setShouldForceRefresh(true);
    }
  }, [loadedAppAt, sourceVersionAtLoad, latestSourceVersion, forceNewAppVersionFlag]);

  // Upon a route change, if we have decided to force a refresh, return false which stops
  // the route change and set window.location to force a page refresh. Otherwise just
  // return true which means let the route change as normal.
  const handleRouteChange = newLocation => {
    if (shouldForceRefresh) {
      window.location.pathname = newLocation.pathname;
      return false;
    } else {
      return true;
    }
  };

  return <Prompt when message={handleRouteChange} />;
};
